import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Container,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { useDropzone } from 'react-dropzone';
import {
  getChatSession,
  sendMessage,
  uploadDocument,
  generateReport,
} from '../services/api';

const PHASE_LABELS = {
  discovery: 'Discovery',
  deep_dive: 'Deep Dive',
  synthesis: 'Synthesis',
  completed: 'Completed',
};

const ChatPage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [phase, setPhase] = useState('discovery');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [error, setError] = useState('');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await getChatSession(contactId);
        setMessages(response.data.messages || []);
        setPhase(response.data.phase || 'discovery');
      } catch (err) {
        setError('Unable to load chat session. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [contactId]);

  const handleSend = async () => {
    if (!inputValue.trim() || sending) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setSending(true);
    setError('');

    // Optimistically add user message
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const response = await sendMessage(contactId, userMessage);
      const { message: assistantMessage, phase: newPhase } = response.data;

      // Update messages with actual response
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempUserMsg.id),
        { ...tempUserMsg, id: response.data.userMessageId || tempUserMsg.id },
        assistantMessage,
      ]);

      if (newPhase) {
        setPhase(newPhase);
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      setUploading(true);
      setError('');

      try {
        for (const file of acceptedFiles) {
          await uploadDocument(contactId, file);
        }
        // Add a system message about the upload
        const uploadMsg = {
          id: `upload-${Date.now()}`,
          role: 'system',
          content: `Document uploaded: ${acceptedFiles.map((f) => f.name).join(', ')}`,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, uploadMsg]);
      } catch (err) {
        setError('Failed to upload document. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [contactId]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
    },
  });

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setError('');

    try {
      await generateReport(contactId);
      navigate(`/report/${contactId}`);
    } catch (err) {
      setError('Failed to generate report. Please try again.');
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      {...getRootProps()}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
      }}
    >
      <input {...getInputProps()} />

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
          px: 3,
          backgroundColor: 'white',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Operational Health Diagnostic
            </Typography>
            <Chip
              label={PHASE_LABELS[phase] || phase}
              color={phase === 'completed' ? 'success' : 'primary'}
              variant={phase === 'completed' ? 'filled' : 'outlined'}
            />
          </Box>
        </Container>
      </Paper>

      {/* Messages Area */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 3 }}>
        <Container maxWidth="md">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent:
                    message.role === 'user'
                      ? 'flex-end'
                      : message.role === 'system'
                      ? 'center'
                      : 'flex-start',
                }}
              >
                {message.role === 'system' ? (
                  <Chip
                    icon={<DescriptionIcon />}
                    label={message.content}
                    variant="outlined"
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  />
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: '75%',
                      px: 2.5,
                      py: 1.5,
                      borderRadius: 2,
                      backgroundColor:
                        message.role === 'user' ? 'primary.main' : 'white',
                      color: message.role === 'user' ? 'white' : 'text.primary',
                      border: message.role === 'assistant' ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ))}

            {sending && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'white',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <CircularProgress size={20} />
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        </Container>
      </Box>

      {/* Input Area */}
      <Paper
        elevation={0}
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          py: 2,
          px: 3,
          backgroundColor: 'white',
        }}
      >
        <Container maxWidth="md">
          {phase === 'completed' ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                Your diagnostic is complete. Generate your personalized report.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleGenerateReport}
                disabled={generatingReport}
                sx={{ minWidth: 200 }}
              >
                {generatingReport ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Generate Report'
                )}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Tooltip title="Attach document">
                <IconButton
                  onClick={open}
                  disabled={uploading}
                  sx={{ color: 'text.secondary' }}
                >
                  {uploading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <AttachFileIcon />
                  )}
                </IconButton>
              </Tooltip>

              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending}
                inputRef={inputRef}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                  },
                }}
              />

              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!inputValue.trim() || sending}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          )}
        </Container>
      </Paper>
    </Box>
  );
};

export default ChatPage;
